Ext.define('SA.data.Project', {
    mixins: {
        manyAttributes: 'SA.data.mixin.ManyAttributes',
        manyLocations: 'SA.data.mixin.ManyLocations',
        observable: 'Ext.util.Observable'
    },

    constructor(cfg) {
        this.mixins.observable.constructor.call(this);

        var data = Object.assign({}, cfg);

        if ('id' in data) {
            this.id = data.id;
            delete data.id;
        }

        data.businessSearches = data.businessSearches || [];
        data.country = data.country || SA.Institution.getLocale();

        data.attributes = data.attributes || [];
        data.locationItems = (data.locationItems || []).map(SA.data.LocationsCache.toLocationItem);

        this.data = data;

        this.ensureHasDiscoverableGeographicUnits();

        this.save = SA.createBuffered(this.save, 2000);
        this.queueSave = SA.Promise.createQueued(this.queueSave);

        if (data.views) {
            data.views = data.views.map(view => SA.data.View.deserialize({project: this, data: view}));
        }
        else {
            data.views = [];
        }

        this.commit(this.capture());
    },

    addBusinessSearch(search) {
        var searches = this.data.businessSearches;

        if (!searches.includes(search)) {
            searches.push(search);

            this.fireEvent('change:businessSearch', this);
        }
    },

    addView(type) {
        return this.addViewWithParams({type, params: {}});
    },

    addViewWithParams({type, params}) {
        var view = SA.data.View.create({type, project: this, params});

        this.data.views.push(view);

        this.addLocations(view.getLocations());

        if (type !== 'businessSearchTable') {
            this.addAttributes(view.getAttributes?.() ?? []);
        }

        this.fireEvent('change:view');

        this.save();

        return view;
    },

    autoCreateViews(types) {
        var getExisting = type => this.getViews().find(view => view.getType() === type),
            countryCode = this.getCountry(),
            attributes = this.getAttributes(),
            locations = this.getLocations(),
            businesses = this.getBusinessSearches(),
            hasAttributes = attributes.length,
            hasBusinesses = businesses.length,
            hasLocations = locations.length,
            views = [];

        if (!hasLocations || !(hasAttributes || hasBusinesses)) {
            return views;
        }

        types = types.filter(type => SA.view.ViewTypes.isTypeAvailableInCountry(type, countryCode));

        if (hasAttributes && types.includes('comparison')) {
            let report = getExisting('comparison');

            if (report) {
                views.push(report);

                ensureAttributes(report);
                ensureLocations(report);
            }
            else {
                views.push(this.addView('comparison'));
            }
        }

        if (hasLocations && types.includes('quickReport')) {
            let report = getExisting('quickReport');

            if (report) {
                views.push(report);

                ensureLocations(report);
            }
            else {
                views.push(this.addView('quickReport'));
            }
        }

        if (types.includes('map') && !getExisting('map')) {
            views.push(this.addView('map'));
        }

        if (hasAttributes && types.includes('ranking')) {
            let report = getExisting('ranking');

            if (report) {
                views.push(report);

                ensureAttributes(report);
                ensureLocation(report);
            }
            else {
                views.push(this.addView('ranking'));
            }
        }

        if (hasBusinesses && types.includes('businessSearchTable')) {
            let report = getExisting('businessSearchTable');

            if (report) {
                views.push(report);

                ensureBusinessSearch(report);
                ensureLocation(report);
            }
            else {
                views.push(this.addView('businessSearchTable'));
            }
        }

        return views;

        function ensureAttributes(view) {
            if (!view.getAttributes().length) {
                attributes.forEach(attribute => {
                    view.addAttribute(attribute);
                });
            }
        }

        function ensureLocations(view) {
            if (!view.getLocations().length) {
                locations.forEach(location => {
                    view.addLocation(location);
                });
            }
        }

        function ensureLocation(view) {
            if (!view.getLocation()) {
                locations.some(location => view.setLocation(location));
            }
        }

        function ensureBusinessSearch(view) {
            if (!view.getBusinessSearch()) {
                view.setBusinessSearch(businesses[0]);
            }
        }
    },

    capture() {
        var data = this.data,
            capture = {};

        Object.entries(data).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value = value.slice();
            }

            capture[key] = value;
        });

        return capture;
    },

    commit(data) {
        this.saved = data;
    },

    createController() {
        return new SA.ProjectController(this);
    },

    createViewOfType(type) {
        var views = this.getViews(),
            view = this.addView(type),
            existingView;

        if (!view.isViewable()) {
            existingView = views.find(vw => vw !== view && vw.getType() === type);

            if (existingView) {
                this.removeView(view);

                view = existingView;
            }
        }

        this.save();

        return view;
    },

    ensureHasDiscoverableGeographicUnits() {
        if (Ext.Array.from(this.data.discoverableGeographicUnits).length > 0) {
            return;
        }

        this.data.discoverableGeographicUnits = SA.data.GeographicUnits.getDefaultDiscoverableUnitsForCountry(this.getCountry());
    },

    erase() {
        Ext.destroyMembers(this, 'controller');

        this.getViews().forEach(view => view.onRemove());

        this.erased = true;
    },

    getAllOptionalDiscoverableUnits() {
        return SA.data.GeographicUnits.getOptionalDiscoverableUnitsForCountry(this.getCountry());
    },

    getAnyMapView() {
        return this.data.views.find(view => view.getType() === 'map');
    },

    getAttributeModels() {
        return this.getAttributes().map(id => SA.data.AttributesCache.getModel(id));
    },

    getAttributesProxy(changeHandler) {
        return new SA.ProjectAttributesProxy({project: this, changeHandler});
    },

    getAttributesReferencedByViews() {
        return SA.Array.unique(this.getViews().map(view => view.getReferencedAttributes()));
    },

    getAvailableEditionsAndUnitsByCensusRelease() {
        var country = SA.Institution.getCountry(this.getCountry());

        return country.censusReleases;
    },

    getBusinessSearches() {
        return this.data.businessSearches;
    },

    getCensusRelease() {
        return this.getCountryModel().getLatestCensusRelease();
    },

    getCensusReleaseGeoUnits(censusReleaseYear) {
        return this.getCountryModel().getGeoUnitsForCensusReleaseYear(censusReleaseYear);
    },

    getController() {
        this.controller = this.controller || this.createController();

        return this.controller;
    },

    getCountry() {
        return this.data.country;
    },

    getCountryModel() {
        return SA.model.Country.get(this.getCountry());
    },

    getDefaultViewName(viewType) {
        var base = SA.view.ViewTypes.getViewNameBase(viewType),
            used = new Set(this.getViews().filter(view => view.getType() === viewType).map(view => view.getName())),
            name = base;

        for (let i = 2; used.has(name); i++) {
            name = `${base} ${i}`;
        }

        return name;
    },

    getDiscoverableGeographicUnits() {
        return this.data.discoverableGeographicUnits;
    },

    getEnabledGeoUnitIds() {
        return this.getDiscoverableGeographicUnits();
    },

    getEnabledGeoUnitsChangeListener(cb) {
        return SA.ListenerFactory.create(this, 'change:unitDiscoverable', cb);
    },

    getEnabledOptionalDiscoverableUnits() {
        var allUnits = this.getDiscoverableGeographicUnits(),
            fixedUnits = this.getFixedDiscoverableUnits();

        return Ext.Array.difference(allUnits, fixedUnits);
    },

    getFixedDiscoverableUnits() {
        return SA.data.GeographicUnits.getFixedDiscoverableUnitsForCountry(this.getCountry());
    },

    getId() {
        return this.id;
    },

    getLatestCensusRelease() {
        var country = SA.Institution.getCountry(this.getCountry());

        return country.getLatestCensusRelease();
    },

    getLocationModels() {
        return this.getLocations().map(stringId => SA.model.LocationFactory.create(stringId));
    },

    getLocationsProxy(changeHandler) {
        return new SA.ProjectLocationsProxy({project: this, changeHandler});
    },

    getName() {
        return this.data.name;
    },

    getProject() {
        return this;
    },

    getReferencedAttributes() {
        return SA.Array.unique([this.getAttributes(), this.getAttributesReferencedByViews()]);
    },

    getSavedAttributes() {
        return this.saved.attributes;
    },

    getType() {
        return 'project';
    },

    getUpdatedTime() {
        return this.data.updated || (new Date().getTime() / 1000);
    },

    getView(uiId) {
        return this.getViews().find(view => view.getUiId() === uiId);
    },

    getViews() {
        return this.data.views;
    },

    hasAttributes() {
        return this.getAttributes().length;
    },

    hasBusinesses() {
        return this.getBusinessSearches().length;
    },

    hasHistoricalCensusRelease() {
        return false;
    },

    hasLocations() {
        return this.getLocations().length;
    },

    isHistoricalViewsCreationEnabled() {
        return this.data.historicalViewsCreationEnabled;
    },

    isSavable() {
        return true;
    },

    isViewable() {
        return true;
    },

    moveView(oldIndex, newIndex) {
        var views = this.data.views;

        if (Ext.isObject(oldIndex)) {
            oldIndex = views.indexOf(oldIndex);


                    }

        newIndex = Ext.Number.constrain(newIndex, 0, views.length - 1);

        if (oldIndex === newIndex) {
            return;
        }

        views.splice(newIndex, 0, views.splice(oldIndex, 1)[0]);

        this.fireEvent('change:view');
    },

    onCustomLocationUpdateImpl(location) {
        this.data.views.forEach(view => {
            view.onCustomLocationUpdate?.(location);
        });
    },

    refreshUpdatedTime() {
        var time = Math.floor(new Date / 1000);

        this.data.updated = time;
        this.saved.updated = time;

        this.fireEvent('change:updated', this);
    },

    refreshViewedTime() {
        this.markUsed = true;

        SA.data.Projects.onProjectViewed(this);

        this.fireEvent('change:viewed', this);

        this.save();
    },

    removeAttributeImpl(attribute) {
        var viewsToRemove = [],
            hasBusinesses = this.hasBusinesses(),
            hasAttributes;

        this.mixins.manyAttributes.removeAttributeImpl.call(this, attribute);

        hasAttributes = this.getAttributes().length;

        this.getViews().forEach(view => {
            switch (view.getType()) {
                case 'comparison':
                case 'ranking':
                case 'radiusTable':
                case 'relatedDataTable':
                case 'timeSeries':
                case 'simmonsCrosstab':
                case 'scarboroughCrosstab':
                case 'scatterPlot':
                case 'barChart':
                case 'histogram':
                    if (hasAttributes) {
                        view.removeAttribute(attribute);
                    }
                    else {
                        viewsToRemove.push(view);
                    }
                break;

                case 'map':
                    if (hasAttributes || hasBusinesses) {
                        if (view.getAttribute() === attribute) {
                            view.addAttribute(null);
                        }
                    }
                    else {
                        viewsToRemove.push(view);
                    }
                break;

                case 'locationQuery':
                    if (!hasAttributes) {
                        viewsToRemove.push(view);
                    }
                break;
            }
        });

        viewsToRemove.forEach(this.removeView, this);

        this.getViews().forEach(view => {
            if (view.auditFilter) {
                view.auditFilter();
            }
        });
    },

    removeAttributes(attributes) {
        var canResolve = () => this.getSavedAttributes().every(attribute => !attributes.includes(attribute));

        attributes.forEach(id => this.removeAttribute(id));

        this.save();

        return new Promise(resolve => {
            var maybeResolve = () => canResolve() ? resolve() : setTimeout(maybeResolve, 50);

            maybeResolve();
        });
    },

    removeBusinessSearch(search) {
        var data = this.data,
            searches = data.businessSearches,
            searchIndex = searches.indexOf(search),
            hasAttributes = this.getAttributes().length,
            viewsToRemove = [],
            hasBusinesses;

        if (searchIndex === -1) {
            return;
        }

        searches = data.businessSearches = searches.slice();

        searches.splice(searchIndex, 1);

        hasBusinesses = searches.length;

        this.getViews().forEach(view => {
            switch (view.getType()) {
                case 'map':
                    if (hasBusinesses || hasAttributes) {
                        if (view.getBusinessSearch() === search) {
                            view.setBusinessSearch(null);
                        }
                    }
                    else {
                        viewsToRemove.push(view);
                    }
                break;

                case 'businessSearchTable':
                    if (hasBusinesses) {
                        if (view.getBusinessSearch() === search) {
                            view.setBusinessSearch(null);
                        }
                    }
                    else {
                        viewsToRemove.push(view);
                    }
                break;
            }
        });

        viewsToRemove.forEach(this.removeView, this);

        this.fireEvent('change:businessSearch', this);
    },

    removeLocationImpl(location) {
        this.mixins.manyLocations.removeLocationImpl.call(this, location);

        if (!this.hasLocations()) {
            this.getViews().filter(view => view.getType() !== 'map').forEach(this.removeView, this);
        }

        this.getViews().slice().forEach(view => {
            view.removeLocation(location);

            if (view.getType() === 'simmonsCrosstab' && !view.getLocation()) {
                this.removeView(view);
            }
        });
    },

    removeView(view) {
        Ext.Array.remove(this.data.views, view);

        this.fireEvent('change:view');

        SA.data.Projects.fireRemovedView(view);

        view.onRemove();
    },

    save() {
        this.queueSave();
    },

    async queueSave() {
        if (this.erased) {
            return;
        }

        this.refreshUpdatedTime();

        var capture = this.capture(),
            saved = this.saved,
            id = this.id,
            saveSelf = true,
            updates, method, views, changedViews, viewCaptures, results, result, resultViews;

        views = capture.views = capture.views.filter(view => {
            if (view.syncGeographicUnits) {
                view.syncGeographicUnits();
            }

            if (view.isSavable()) {
                return true;
            }

            view.setId(null);

            return false;
        });

        if (id) {
            method = 'update';
            updates = {};

            for (let field in capture) {
                if (!equals(capture[field], saved[field])) {
                    updates[field] = capture[field];
                }
            }

            if (this.markUsed) {
                this.markUsed = false;

                updates.used = true;
            }

            if (SA.Object.isEmpty(updates)) {
                saveSelf = false;
            }

            updates.id = id;
        }
        else {
            method = 'create';
            updates = Object.assign({}, capture);
        }

        changedViews = updates.views || views.some(view => view.isChanged());

        if (changedViews) {
            viewCaptures = views.map(view => view.capture());

            updates.views = views.map((view, index) => {
                return view.getUpdates(viewCaptures[index]);
            });
        }
        else if (!saveSelf) {
            return;
        }

        results = await SA.Ajax.request({resource: 'user/projects', method, data: [updates]});

        result = results[0];
        resultViews = result.views;

        this.commit(capture);

        this.setId(result.id);

        if (updates.views) {
            resultViews.forEach((resultView, index) => {
                let view = views[index];

                view.commit(viewCaptures[index]);
                view.setId(resultView.id);
            });

            views.forEach(view => {
                view.fireEvent('save', view);
            });
        }

        if (saveSelf) {
            this.fireEvent('save', this);
        }

        this.save();

        function equals(value1, value2) {
            if (value1 === value2) {
                return true;
            }

            if (Array.isArray(value1) && Array.isArray(value2)) {
                return Ext.Array.equals(value1, value2);
            }

            return false;
        }
    },

    setCountry(countryCode) {
        this.data.country = countryCode;

        this.data.discoverableGeographicUnits = null;
        this.ensureHasDiscoverableGeographicUnits();
    },

    setHistoricalViewsCreationEnabled(historicalViewsCreationEnabled) {
        this.data.historicalViewsCreationEnabled = historicalViewsCreationEnabled;
    },

    setId(id) {
        this.id = id;
    },

    setName(name) {
        if (name.trim() === '') {
            return;
        }

        var data = this.data;

        if (data.name !== name) {
            data.name = name;

            this.fireEvent('change:name', this, name);
        }
    },

    setUnitDiscoverable(unit, discoverable) {
        if (discoverable) {
            Ext.Array.include(this.data.discoverableGeographicUnits, unit);
        }
        else {
            Ext.Array.remove(this.data.discoverableGeographicUnits, unit);
        }

        this.fireEvent('change:unitDiscoverable');

        this.save();
    }
});
